// The player dialog (spec/13 §8, F-ORG-05/-06, F-IAM-07, F-CMP-02): one player, in a large centred dialog
// over the list (2026-10-07: a dialog, not a sheet from the side). Tabs — who they are (with their tags, ID
// check and ban), their history, their wallet, and this organisation's notes about them. Each editable part is
// behind its own permission; the server checks again (spec/13 §1). Nothing opens a second dialog on top: an ID
// check is recorded in place in its section, a ban in place in its own. The wallet tab is where a player's
// wallet is run (F-WAL-01/-02/-06/-07/-08): the balance and statement, and — at a venue — Top up, Adjust and
// Refund, each a step in place on the tab (WalletActions; the Wallet screen folded in here, 2026-10-07); and
// what the player owes on account (CreditSection, F-POS-07). Where the organisation's wallet is off (the balance
// read's `enabled`), the tab says so first, with the way to the setting, and offers no action the server would refuse.

import type { IdCheck, PlayerBan, PlayerNote } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Avatar, Badge, Button, Chip, Dialog, EmptyState, InfoTip, Skeleton, StatCard, Tabs, TextField } from '@clavian/ui-web';
import { useState, type FormEvent, type ReactNode } from 'react';

import { useAccess, useMyVenues } from '../../api/queries';
import { useProblem } from '../../api/use-problem';
import { LoadFailure } from '../../shell/LoadFailure';
import { Icon } from '../../shell/nav-icons';
import { useScopeIds } from '../../shell/scope';
import { Facts, Notice, Section, SummaryStrip, useStepFocus } from '../../ui';
import { TickingMoney } from '../../ui/ticking';
import { PlayerAuditTab } from '../audit/PlayerAuditTab';
import { useWalletBalance, useWalletStatement } from '../wallet/queries';
import { CreditSection } from './CreditSection';
import { GamesTab } from './GamesTab';
import { IdCheckForm } from './IdCheckForm';
import { useWalletSettingsLink, WalletActions } from './WalletActions';
import {
  playerName,
  useBanPlayer,
  useIdChecks,
  usePlayerBans,
  usePlayerOrders,
  usePlayerPasses,
  usePlayerPoints,
  usePlayerProfile,
  usePlayerSessions,
  useUnbanPlayer,
  useUpdatePlayer,
} from './queries';

const PLAYERS_NOTES = 'players:notes';
const PLAYERS_BAN = 'players:ban';
const PLAYERS_ID_CHECK = 'players:id_check';
const AUDIT_READ = 'audit:read';

export function PlayerDialog({ playerId, onClose }: { readonly playerId: string | null; readonly onClose: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const scope = useScopeIds();
  // The dialog is titled with the player's own name once it is read (the same cached read the body
  // uses), so the header never says "Player" above the name it is about.
  const profile = usePlayerProfile(scope.organizationId, playerId ?? undefined);
  const name = playerId !== null && profile.data !== undefined ? playerName(profile.data) : t('players.drawer.title');
  return (
    <Dialog
      open={playerId !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      // xl: a tabbed detail, one height so the tabs do not make it jump.
      size="xl"
      eyebrow={t('players.drawer.eyebrow')}
      title={name}
    >
      {playerId !== null ? <PlayerDetail playerId={playerId} /> : null}
    </Dialog>
  );
}

function PlayerDetail({ playerId }: { readonly playerId: string }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const scope = useScopeIds();
  const access = useAccess(scope.access);
  const profile = usePlayerProfile(scope.organizationId, playerId);

  if (profile.data === undefined) {
    return profile.isError ? (
      <LoadFailure error={profile.error} message={t('players.drawer.unavailable')} onRetry={() => void profile.refetch()} />
    ) : (
      <div className="console-people-detail">
        <Skeleton shape="text" width="12rem" height="1.5rem" />
        <Skeleton height="8rem" />
      </div>
    );
  }

  const player = profile.data;
  return (
    <div className="console-people-detail">
      <header className="console-people-head">
        <Avatar name={playerName(player)} size="lg" decorative />
        <div className="console-people-head__who">
          {/* The handle under the name the title already shows — not when the name *is* the handle. */}
          {player.username !== null && player.username !== '' && player.username !== playerName(player) ? <span className="console-subtle">@{player.username}</span> : null}
          <span className="console-subtle">{t('players.drawer.knownSince', { date: format.date(player.knownSince, 'long') })}</span>
        </div>
      </header>

      <Tabs
        label={t('players.drawer.tabs')}
        items={[
          { value: 'overview', label: t('players.drawer.tab.overview'), content: <OverviewTab playerId={playerId} tags={player.tags} phone={player.phoneNumber} email={player.email} /> },
          { value: 'sessions', label: t('players.drawer.tab.sessions'), content: <SessionsTab playerId={playerId} /> },
          // F-CAT-04: the titles the player played, as the stations detected them.
          { value: 'games', label: t('players.drawer.tab.games'), content: <GamesTab playerId={playerId} /> },
          { value: 'orders', label: t('players.drawer.tab.orders'), content: <OrdersTab playerId={playerId} /> },
          { value: 'passes', label: t('players.drawer.tab.passes'), content: <PassesTab playerId={playerId} /> },
          { value: 'wallet', label: t('players.drawer.tab.wallet'), content: <WalletTab playerId={playerId} name={playerName(player)} /> },
          { value: 'points', label: t('players.drawer.tab.points'), content: <PointsTab playerId={playerId} /> },
          { value: 'notes', label: t('players.drawer.tab.notes'), content: <NotesTab playerId={playerId} notes={player.notes} /> },
          // The Audit tab rides `audit:read` (spec/13 §8 rule 5, F-ORG-14); a role without it is not offered the tab.
          ...(access.can(AUDIT_READ) ? [{ value: 'audit', label: t('players.drawer.tab.audit'), content: <PlayerAuditTab playerId={playerId} /> }] : []),
        ]}
      />
    </div>
  );
}

// -------------------------------------------------------------------------------------------------
// Overview: facts, tags, ID check and the ban
// -------------------------------------------------------------------------------------------------

function OverviewTab({ playerId, tags, phone, email }: { readonly playerId: string; readonly tags: readonly string[]; readonly phone: string | null; readonly email: string | null }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <div className="console-people-tab">
      <Section title={t('players.facts.title')} tip={t('players.contactMaskedNote')}>
        <Facts
          items={[
            { key: 'phone', label: t('players.column.phone'), value: phone ?? <span className="console-subtle">{t('players.notGiven')}</span> },
            { key: 'email', label: t('players.column.email'), value: email ?? <span className="console-subtle">{t('players.notGiven')}</span> },
          ]}
        />
      </Section>
      <TagEditor playerId={playerId} tags={tags} />
      <IdCheckSection playerId={playerId} />
      <BanSection playerId={playerId} />
    </div>
  );
}

function TagEditor({ playerId, tags }: { readonly playerId: string; readonly tags: readonly string[] }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const scope = useScopeIds();
  const access = useAccess(scope.access);
  const canEdit = access.status === 'ready' && access.can(PLAYERS_NOTES);
  const update = useUpdatePlayer(scope.organizationId, playerId);
  // Adding happens in the row: "+ Add tag" becomes a small field where the chip would be. Enter adds,
  // Escape (or leaving it empty) puts the chip back — no separate form under the tags.
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  function close(): void {
    setAdding(false);
    setDraft('');
  }

  function add(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const tag = draft.trim();
    if (tag === '') {
      close();
      return;
    }
    if (tags.some((existing) => existing.toLowerCase() === tag.toLowerCase())) {
      setError(t('players.tags.duplicate'));
      return;
    }
    setError(undefined);
    // Stay open after a tag lands, so several can be typed in a row.
    update.mutate({ tags: { add: [tag] } }, { onSuccess: () => setDraft(''), onError: (failure) => setError(problem.text(failure, { fallback: t('players.tags.failed') })) });
  }

  return (
    <Section title={t('players.tags.title')} tip={t('players.tags.hint')}>
      <ul className="console-people-tags console-people-tags--editable" aria-label={t('players.tags.listLabel')}>
        {tags.length === 0 && !canEdit ? <li className="console-subtle">{t('players.tags.none')}</li> : null}
        {tags.map((tag) => (
          <li key={tag}>
            {canEdit ? (
              <button
                type="button"
                className="console-tag-remove"
                onClick={() => update.mutate({ tags: { remove: [tag] } }, { onError: (failure) => setError(problem.text(failure, { fallback: t('players.tags.failed') })) })}
                aria-label={t('players.tags.remove', { tag })}
              >
                {tag}
                <span aria-hidden="true" className="console-tag-remove__x">
                  <Icon name="close" />
                </span>
              </button>
            ) : (
              <Chip>{tag}</Chip>
            )}
          </li>
        ))}
        {canEdit ? (
          <li>
            {adding ? (
              // `data-owns-escape`: Escape puts the chip back, and the dialog stays open (the kit's `ownsEscape`).
              <form className="console-tag-add" data-owns-escape onSubmit={add} noValidate>
                <input
                  // The field replaces the chip the staff member just pressed, so focus follows it there.
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  className="console-tag-add__input"
                  aria-label={t('players.tags.addLabel')}
                  placeholder={t('players.tags.addPlaceholder')}
                  value={draft}
                  maxLength={32} // the contract's PLAYER_TAG_MAX_LENGTH; the server checks it again
                  onChange={(event) => {
                    setDraft(event.target.value);
                    setError(undefined);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') close();
                  }}
                  onBlur={() => {
                    if (draft.trim() === '') close();
                  }}
                  aria-invalid={error === undefined ? undefined : true}
                  aria-describedby={error === undefined ? undefined : `player-tag-error-${playerId}`}
                />
              </form>
            ) : (
              <button type="button" className="console-tag-new" onClick={() => setAdding(true)}>
                <Icon name="plus" />
                {t('players.tags.add')}
              </button>
            )}
          </li>
        ) : null}
      </ul>
      {error !== undefined ? (
        <p className="console-field-error" role="alert" id={`player-tag-error-${playerId}`}>
          {error}
        </p>
      ) : null}
    </Section>
  );
}

function IdCheckSection({ playerId }: { readonly playerId: string }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const scope = useScopeIds();
  const access = useAccess(scope.access);
  const canRead = access.status === 'ready' && access.can(PLAYERS_ID_CHECK);
  const checks = useIdChecks(scope.organizationId, canRead ? playerId : undefined);
  const [recording, setRecording] = useState(false);
  // Recorded in place, under the section's facts: focus goes into the form, and back to "Record a check" after.
  const { stepRef, opener } = useStepFocus(recording);

  if (!canRead) return null;

  let body: ReactNode;
  if (checks.data === undefined) {
    body = checks.isError ? <LoadFailure error={checks.error} message={t('players.idCheck.unavailable')} onRetry={() => void checks.refetch()} /> : <Skeleton shape="text" lines={2} />;
  } else if (checks.data.length === 0) {
    body = <p className="console-subtle">{t('players.idCheck.none')}</p>;
  } else {
    const latest = checks.data[0] as IdCheck;
    body = (
      <div className="console-people-idcheck">
        <Badge tone="ok" dot>
          {t('players.idCheck.verified')}
        </Badge>
        <span className="console-subtle">
          {t(`players.idCheck.method.${latest.method}`)} · {format.dateTime(latest.verifiedAt, { date: 'medium', time: 'short' })}
        </span>
        {latest.document === null ? null : (
          <span className="console-subtle">
            {t(`players.idCheck.document.${latest.document.type}`)} ···· <span className="console-num">{latest.document.last4}</span>
          </span>
        )}
      </div>
    );
  }

  return (
    <Section
      title={t('players.idCheck.title')}
      actions={
        recording ? null : (
          <Button {...opener} variant="secondary" size="sm" icon={<Icon name="check" />} onClick={() => setRecording(true)}>
            {t('players.idCheck.recordAction')}
          </Button>
        )
      }
    >
      {body}
      {recording ? (
        <div ref={stepRef}>
          <IdCheckForm playerId={playerId} onDone={() => setRecording(false)} />
        </div>
      ) : null}
    </Section>
  );
}

function BanSection({ playerId }: { readonly playerId: string }): ReactNode {
  const { t } = useTranslation('console');
  const scope = useScopeIds();
  const access = useAccess(scope.access);
  const canBan = access.status === 'ready' && access.can(PLAYERS_BAN);
  const bans = usePlayerBans(scope.organizationId, canBan ? playerId : undefined);
  const [action, setAction] = useState<'none' | 'ban' | 'lift'>('none');

  if (!canBan) return null;

  const active = (bans.data ?? []).filter((ban) => ban.status === 'active');
  // The ban this scope can lift: the venue's own at a venue, the organisation's above it.
  const inScope = active.find((ban) => (scope.venueId === undefined ? ban.scope === 'organization' : ban.scope === 'venue' && ban.venueId === scope.venueId));

  return (
    <Section title={t('players.ban.title')}>
      {bans.data === undefined ? (
        bans.isError ? (
          <LoadFailure error={bans.error} message={t('players.ban.unavailable')} onRetry={() => void bans.refetch()} />
        ) : (
          <Skeleton shape="text" lines={2} />
        )
      ) : (
        <>
          {active.length === 0 ? (
            <p className="console-subtle">{t('players.ban.none')}</p>
          ) : (
            <ul className="console-people-bans" aria-label={t('players.ban.listLabel')}>
              {active.map((ban) => (
                <li key={ban.id}>
                  <BanRow ban={ban} />
                </li>
              ))}
            </ul>
          )}

          {action === 'none' ? (
            inScope === undefined ? (
              <Button variant="danger" icon={<Icon name="lock" />} onClick={() => setAction('ban')}>
                {scope.venueId === undefined ? t('players.ban.banOrg') : t('players.ban.banVenue')}
              </Button>
            ) : (
              <Button variant="secondary" icon={<Icon name="unlock" />} onClick={() => setAction('lift')}>
                {scope.venueId === undefined ? t('players.ban.liftOrg') : t('players.ban.liftVenue')}
              </Button>
            )
          ) : (
            <BanForm playerId={playerId} mode={action} onDone={() => setAction('none')} onCancel={() => setAction('none')} />
          )}
        </>
      )}
    </Section>
  );
}

function BanRow({ ban }: { readonly ban: PlayerBan }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  return (
    <div className="console-people-ban">
      <Badge tone="danger" dot>
        {t(`players.ban.scope.${ban.scope}`)}
      </Badge>
      <span className="console-people-ban__reason">{ban.reason}</span>
      <span className="console-subtle">{ban.expiresAt === null ? t('players.ban.noExpiry') : t('players.ban.until', { date: format.date(ban.expiresAt, 'medium') })}</span>
    </div>
  );
}

function BanForm({ playerId, mode, onDone, onCancel }: { readonly playerId: string; readonly mode: 'ban' | 'lift'; readonly onDone: () => void; readonly onCancel: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const scope = useScopeIds();
  const ban = useBanPlayer(scope.organizationId, playerId, scope.venueId);
  const unban = useUnbanPlayer(scope.organizationId, playerId, scope.venueId);
  const mutation = mode === 'ban' ? ban : unban;
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const text = reason.trim();
    if (text === '') {
      setError(t('players.ban.reasonRequired'));
      return;
    }
    setError(undefined);
    mutation.mutate({ reason: text }, { onSuccess: onDone, onError: (failure) => setError(problem.text(failure, { fallback: t('players.ban.actionFailed') })) });
  }

  return (
    <form className="console-form" onSubmit={submit} noValidate>
      <Notice tone={mode === 'ban' ? 'warn' : 'info'} role="note">
        {mode === 'ban'
          ? scope.venueId === undefined
            ? t('players.ban.banOrgHint')
            : t('players.ban.banVenueHint')
          : t('players.ban.liftHint')}
      </Notice>
      <TextField label={t('players.ban.reasonLabel')} description={t('players.ban.reasonHint')} required value={reason} onChange={(event) => setReason(event.target.value)} {...(error === undefined ? {} : { error })} />
      <div className="console-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant={mode === 'ban' ? 'danger' : 'primary'} busy={mutation.isPending}>
          {mode === 'ban' ? t('players.ban.confirmBan') : t('players.ban.confirmLift')}
        </Button>
      </div>
    </form>
  );
}

// -------------------------------------------------------------------------------------------------
// Wallet
// -------------------------------------------------------------------------------------------------

function WalletTab({ playerId, name }: { readonly playerId: string; readonly name: string }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const scope = useScopeIds();
  const balance = useWalletBalance(scope.organizationId, playerId);
  const statement = useWalletStatement(scope.organizationId, playerId);
  const rows = (statement.data?.pages ?? []).flatMap((page) => page.data);

  return (
    <div className="console-people-tab">
      {/* The organisation's wallet is off (`wallet.enabled`): said first, with where to turn it on, and no actions the
          server would refuse. Otherwise Top up, Adjust and Refund (F-WAL-02/-06/-07): at a venue, where the money
          changes hands; at the organisation, a note says so. */}
      {balance.data?.enabled === false ? (
        <WalletOff />
      ) : scope.venueId === undefined ? (
        <WalletAtVenueNote />
      ) : (
        <WalletActions player={{ id: playerId, name }} venue={{ organizationId: scope.organizationId, venueId: scope.venueId, currency: scope.currency ?? 'INR' }} />
      )}
      {balance.data === undefined ? (
        balance.isError ? (
          <LoadFailure error={balance.error} message={t('players.wallet.unavailable')} onRetry={() => void balance.refetch()} />
        ) : (
          <div className="console-summary" aria-hidden="true">
            <Skeleton height="5.75rem" />
            <Skeleton height="5.75rem" />
            <Skeleton height="5.75rem" />
          </div>
        )
      ) : (
        <>
          {/* The balance as the summary strip (ADR-0043): the figures tick to a top-up's or a spend's new amount. */}
          <SummaryStrip label={t('players.wallet.balanceLabel')}>
            <StatCard label={t('players.wallet.total')} value={<TickingMoney className="console-num" value={balance.data.total} format={(money) => format.money(money)} />} />
            <StatCard label={t('players.wallet.cash')} value={<TickingMoney className="console-num" value={balance.data.cash} format={(money) => format.money(money)} />} />
            <StatCard label={t('players.wallet.bonus')} value={<TickingMoney className="console-num" value={balance.data.bonus} format={(money) => format.money(money)} />} />
          </SummaryStrip>
          {/* An older server does not say: then nothing is shown, rather than a guess. */}
          {balance.data.useForPlay === undefined ? null : <WalletUseForPlay on={balance.data.useForPlay} />}
        </>
      )}

      {/* What the player owes (F-POS-07): at a venue, behind `orders:read`. */}
      <CreditSection playerId={playerId} />

      <Section title={t('players.wallet.statement')}>
        {statement.data === undefined ? (
          statement.isError ? (
            <LoadFailure error={statement.error} message={t('players.wallet.statementUnavailable')} onRetry={() => void statement.refetch()} />
          ) : (
            <Skeleton shape="text" lines={4} />
          )
        ) : rows.length === 0 ? (
          <EmptyState size="compact" title={t('players.wallet.empty')} />
        ) : (
          <>
            <ul className="console-people-lines">
              {rows.map((row) => (
                <li key={row.id} className="console-people-line">
                  <span className="console-people-line__what">
                    <span>{row.description}</span>
                    <span className="console-subtle">{format.dateTime(row.occurredAt, { date: 'medium', time: 'short' })}</span>
                  </span>
                  <span className="console-num">{format.money(row.amount)}</span>
                </li>
              ))}
            </ul>
            {statement.hasNextPage ? (
              <div className="console-list-foot">
                <Button variant="ghost" size="sm" busy={statement.isFetchingNextPage} onClick={() => void statement.fetchNextPage()}>
                  {t('players.wallet.more')}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </Section>
    </div>
  );
}

/**
 * The organisation's wallet is off (`wallet.enabled`, off until an owner turns it on; spec/06 §5): what that means here,
 * and — for someone who manages payments — the way to the setting. Top up, Adjust and Refund are not offered: the server
 * would refuse each (`wallet-disabled`).
 */
function WalletOff(): ReactNode {
  const { t } = useTranslation('console');
  const settingsLink = useWalletSettingsLink();
  return (
    <Notice role="note" title={t('wallet.off.title')} action={settingsLink?.('wallet.enabled', t('wallet.off.turnOn'))}>
      {t('wallet.off.body')}
      {settingsLink === undefined ? ` ${t('wallet.off.askOwner')}` : null}
    </Notice>
  );
}

/** At the organisation the tab reads only: for a member who moves wallet money, where it is done instead. */
function WalletAtVenueNote(): ReactNode {
  const { t } = useTranslation('console');
  const scope = useScopeIds();
  const access = useAccess(scope.access);
  const movesMoney = access.status === 'ready' && ['wallet:topup', 'wallet:adjust', 'wallet:refund'].some((permission) => access.can(permission));
  return movesMoney ? <Notice role="note">{t('wallet.atVenue')}</Notice> : null;
}

/**
 * "Use my wallet for play time" (F-WAL-04): the player's own choice, per organisation — whether the wallet pays for
 * play on its own once their paid time and passes run out. Staff read it here and never change it: there is no
 * control, because only the player sets it, at the kiosk.
 */
function WalletUseForPlay({ on }: { readonly on: boolean }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <dl className="console-people-wallet-use">
      <dt>{t('players.wallet.useForPlay.label')}</dt>
      <dd>
        <Badge tone={on ? 'info' : 'neutral'} dot>
          {on ? t('players.wallet.useForPlay.on') : t('players.wallet.useForPlay.off')}
        </Badge>
        <span className="console-subtle">
          {on ? t('players.wallet.useForPlay.onHint') : t('players.wallet.useForPlay.offHint')} {t('players.wallet.useForPlay.playerChanges')}
        </span>
      </dd>
    </dl>
  );
}

// -------------------------------------------------------------------------------------------------
// History tabs: sessions, orders, passes and points (spec/13 §8, F-ORG-05). Read-only lists of what the
// player has done across the organisation; the M3 features (F-SES, F-POS, F-PRC, F-LOY) fill the rows.
// -------------------------------------------------------------------------------------------------

/** Resolve a history row's venue id to its name; `null` (organisation-wide) reads as "Every venue". */
function useVenueName(): (venueId: string | null) => string {
  const { t } = useTranslation('console');
  const venues = useMyVenues();
  const byId = new Map((venues.data ?? []).map((venue) => [venue.id, venue.name]));
  return (venueId) => (venueId === null ? t('players.history.everyVenue') : (byId.get(venueId) ?? venueId));
}

/** The shape a history tab reads off any of the four infinite queries. */
interface HistoryQuery<T> {
  readonly data: { readonly pages: readonly { readonly data: readonly T[] }[] } | undefined;
  readonly isError: boolean;
  readonly error: unknown;
  readonly hasNextPage: boolean;
  readonly isFetchingNextPage: boolean;
  refetch: () => unknown;
  fetchNextPage: () => unknown;
}

/** A history tab's frame: the skeleton, the load failure, the empty state, the list and load-more. */
function HistoryFrame<T extends { id: string }>({
  query,
  title,
  unavailable,
  empty,
  row,
}: {
  readonly query: HistoryQuery<T>;
  readonly title: string;
  readonly unavailable: string;
  readonly empty: string;
  readonly row: (entry: T) => ReactNode;
}): ReactNode {
  const { t } = useTranslation('console');
  const rows = (query.data?.pages ?? []).flatMap((page) => [...page.data]);
  return (
    <div className="console-people-tab">
      {/* The tab already names this list ("Sessions", "Orders"…): the card carries no title that says it again. */}
      <Section>
        {query.data === undefined ? (
          query.isError ? (
            <LoadFailure error={query.error} message={unavailable} onRetry={() => void query.refetch()} />
          ) : (
            <Skeleton shape="text" lines={4} />
          )
        ) : rows.length === 0 ? (
          <EmptyState size="compact" title={empty} />
        ) : (
          <>
            <ul className="console-people-lines" aria-label={title}>
              {rows.map(row)}
            </ul>
            {query.hasNextPage ? (
              <div className="console-list-foot">
                <Button variant="ghost" size="sm" busy={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
                  {t('players.history.more')}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </Section>
    </div>
  );
}

/** The sale and holding states with words of their own; anything newer reads as "other". */
const ORDER_STATUSES = new Set(['placed', 'partially_paid', 'paid', 'partially_refunded', 'refunded', 'cancelled']);
const PASS_STATUSES = new Set(['exhausted', 'expired', 'refunded', 'voided']);

function SessionsTab({ playerId }: { readonly playerId: string }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const scope = useScopeIds();
  const venueName = useVenueName();
  const query = usePlayerSessions(scope.organizationId, playerId);
  return (
    <HistoryFrame
      query={query}
      title={t('players.drawer.tab.sessions')}
      unavailable={t('players.history.unavailable')}
      empty={t('players.history.noSessions')}
      row={(session) => (
        // A session still running carries the live rail at its edge, as its tile does on the floor (ADR-0043).
        <li key={session.id} className={session.endedAt === null ? 'console-people-line console-rail console-rail--start' : 'console-people-line'} data-rail={session.endedAt === null ? 'live' : undefined}>
          <span className="console-people-line__what">
            <span>
              {session.stationNumber === null
                ? venueName(session.venueId)
                : t('players.history.sessionAt', { station: session.stationNumber, venue: venueName(session.venueId) })}
            </span>
            <span className="console-subtle">
              {format.dateTime(session.startedAt, { date: 'medium', time: 'short' })} · {format.duration(session.playedSeconds)}
            </span>
          </span>
          <span className="console-subtle">{session.endedAt === null ? t('players.history.running') : t('players.history.ended')}</span>
        </li>
      )}
    />
  );
}

function OrdersTab({ playerId }: { readonly playerId: string }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const scope = useScopeIds();
  const venueName = useVenueName();
  const query = usePlayerOrders(scope.organizationId, playerId);
  return (
    <HistoryFrame
      query={query}
      title={t('players.drawer.tab.orders')}
      unavailable={t('players.history.unavailable')}
      empty={t('players.history.noOrders')}
      row={(order) => (
        <li key={order.id} className="console-people-line">
          <span className="console-people-line__what">
            <span>{order.note ?? venueName(order.venueId)}</span>
            <span className="console-subtle">
              {format.dateTime(order.placedAt, { date: 'medium', time: 'short' })} · {t(`players.history.channel.${order.channel === 'kiosk' ? 'kiosk' : 'counter'}`)} ·{' '}
              {t(`players.history.orderStatus.${ORDER_STATUSES.has(order.status) ? order.status : 'other'}`)}
            </span>
          </span>
          <span className="console-num">{format.money(order.total)}</span>
        </li>
      )}
    />
  );
}

function PassesTab({ playerId }: { readonly playerId: string }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const scope = useScopeIds();
  const query = usePlayerPasses(scope.organizationId, playerId);
  return (
    <HistoryFrame
      query={query}
      title={t('players.drawer.tab.passes')}
      unavailable={t('players.history.unavailable')}
      empty={t('players.history.noPasses')}
      row={(pass) => (
        <li key={pass.id} className="console-people-line">
          <span className="console-people-line__what">
            <span>{pass.name}</span>
            <span className="console-subtle">
              {pass.expiresAt === null ? t('players.history.noExpiry') : t('players.history.expires', { date: format.date(pass.expiresAt, 'medium') })}
            </span>
          </span>
          <span className="console-subtle">
            {pass.status === 'active'
              ? t('players.history.minutesLeft', { count: Math.round(pass.remainingSeconds / 60) })
              : t(`players.history.passStatus.${PASS_STATUSES.has(pass.status) ? pass.status : 'other'}`)}
          </span>
        </li>
      )}
    />
  );
}

function PointsTab({ playerId }: { readonly playerId: string }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const scope = useScopeIds();
  const venueName = useVenueName();
  const query = usePlayerPoints(scope.organizationId, playerId);
  return (
    <HistoryFrame
      query={query}
      title={t('players.drawer.tab.points')}
      unavailable={t('players.history.unavailable')}
      empty={t('players.history.noPoints')}
      row={(entry) => (
        <li key={entry.id} className="console-people-line">
          <span className="console-people-line__what">
            <span>{venueName(entry.venueId)}</span>
            <span className="console-subtle">{format.dateTime(entry.occurredAt, { date: 'medium', time: 'short' })}</span>
          </span>
          <span className="console-num">{t('players.history.points', { count: entry.points })}</span>
        </li>
      )}
    />
  );
}

// -------------------------------------------------------------------------------------------------
// Notes
// -------------------------------------------------------------------------------------------------

function NotesTab({ playerId, notes }: { readonly playerId: string; readonly notes: readonly PlayerNote[] }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const format = useVenueFormatter();
  const scope = useScopeIds();
  const access = useAccess(scope.access);
  const canEdit = access.status === 'ready' && access.can(PLAYERS_NOTES);
  const update = useUpdatePlayer(scope.organizationId, playerId);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  function add(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const body = draft.trim();
    if (body === '') return;
    setError(undefined);
    update.mutate({ notes: { add: [{ body }] } }, { onSuccess: () => setDraft(''), onError: (failure) => setError(problem.text(failure, { fallback: t('players.notes.failed') })) });
  }

  return (
    <div className="console-people-tab">
      <div className="console-switch-tip">
        <InfoTip>{t('players.notes.hint')}</InfoTip>
      </div>
      {notes.length === 0 ? (
        <EmptyState size="compact" icon={<Icon name="message" />} title={t('players.notes.empty')} />
      ) : (
        <ul className="console-people-notes" aria-label={t('players.notes.listLabel')}>
          {notes.map((note) => (
            <li key={note.id} className="console-people-note">
              <div className="console-people-note__meta">
                <span className="console-people-note__author">{note.author.name ?? t('players.notes.unknownAuthor')}</span>
                <span className="console-subtle">{format.dateTime(note.createdAt, { date: 'medium', time: 'short' })}</span>
                {canEdit ? (
                  <Button variant="ghost" size="sm" onClick={() => update.mutate({ notes: { remove: [note.id] } }, { onError: (failure) => setError(problem.text(failure, { fallback: t('players.notes.failed') })) })}>
                    {t('common.remove')}
                  </Button>
                ) : null}
              </div>
              <p className="console-people-note__body">{note.body}</p>
            </li>
          ))}
        </ul>
      )}
      {canEdit ? (
        // A composer, not a form: the note is written in place and Save appears once there is something
        // to save. Ctrl/⌘ + Enter saves too.
        <form className="console-note-composer" onSubmit={add} noValidate>
          <textarea
            className="console-note-composer__input"
            aria-label={t('players.notes.addLabel')}
            placeholder={t('players.notes.addPlaceholder')}
            rows={draft.trim() === '' ? 1 : 3}
            value={draft}
            maxLength={2000} // the contract's PLAYER_NOTE_MAX_LENGTH; the server checks it again
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                event.preventDefault();
                event.currentTarget.form?.requestSubmit();
              }
            }}
            aria-invalid={error === undefined ? undefined : true}
            aria-describedby={error === undefined ? undefined : `player-note-error-${playerId}`}
          />
          {error !== undefined ? (
            <p className="console-field-error" role="alert" id={`player-note-error-${playerId}`}>
              {error}
            </p>
          ) : null}
          {draft.trim() === '' ? null : (
            <div className="console-note-composer__actions">
              <Button type="button" variant="ghost" size="sm" onClick={() => setDraft('')}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="primary" size="sm" busy={update.isPending}>
                {t('players.notes.add')}
              </Button>
            </div>
          )}
        </form>
      ) : (
        <p className="console-subtle">{t('players.notes.readOnly')}</p>
      )}
    </div>
  );
}
